import { useMutation, useQueryClient } from '@tanstack/react-query'
import { postData } from '../utils'
import { TankValuesArrayType } from '../types/apiResponse/tankTypes'
import { toApiStockFry } from '../types/mappers/tank.mapper'
import type { StockFryCommand } from '../types/domain/tank'
import { getBatches } from '../api/batch/batch.service'
import { toast } from '@/utils/toast'

// Helper to convert yyyyMMdd number to Date object
const parseMoveDate = (moveDate: number): Date => {
  const dateStr = String(moveDate)
  if (dateStr.length === 8) {
    // yyyyMMdd format (e.g., 20241208)
    const year = parseInt(dateStr.substring(0, 4))
    const month = parseInt(dateStr.substring(4, 6)) - 1
    const day = parseInt(dateStr.substring(6, 8))
    return new Date(year, month, day)
  }
  // Fallback: treat as timestamp
  return new Date(moveDate)
}

// Helper to normalize groupId (convert virtual keys to null)
const normalizeGroupId = (groupId: string | undefined | null): string | null => {
  if (!groupId || groupId === '$empty' || groupId === '$ungrouped') {
    return null
  }
  return groupId
}

// Helper to normalize groupName (convert virtual keys to null)
const normalizeGroupName = (groupName: string | undefined | null): string | null => {
  if (!groupName || groupName === '$empty' || groupName === '$ungrouped') {
    return null
  }
  return groupName
}

export type StockResult = { tankId: string; schoolId: string }

const stockAPI = async (tankInfo: TankValuesArrayType): Promise<StockResult[]> => {
  const results: StockResult[] = []

  // Fetch existing batches to resolve batch names
  const existingBatches = await getBatches()
  let batchByName = new Map(existingBatches.map((b) => [b.name, b.id]))

  // V2 API: 각 tank별로 개별 호출
  for (const tank of tankInfo) {
    // Resolve batch input against existing batches
    const batchInput = tank.batchInput?.trim() || null
    let resolvedBatchId: string | null = null
    let resolvedBatchName: string | null = null

    if (batchInput) {
      const existingId = batchByName.get(batchInput)
      if (existingId) {
        resolvedBatchId = existingId
      } else {
        resolvedBatchName = batchInput
      }
    }

    const command: StockFryCommand = {
      tankId: tank.tankId,
      tankName: tank.tankName || '',
      groupId: normalizeGroupId(tank.batchId),
      groupName: normalizeGroupName(tank.batchName),
      resolvedBatchId,
      resolvedBatchName,
      count: tank.count || 0,
      sampleWeight: tank.sampleWeight || 0,
      startDate: tank.moveDate ? parseMoveDate(tank.moveDate) : new Date(),
    }

    const apiRequest = toApiStockFry(command)
    const response = await postData<{ schoolId: string }>(`/tank/${tank.tankId}/stock-fry`, apiRequest)
    if (response?.schoolId) {
      results.push({ tankId: tank.tankId, schoolId: response.schoolId })
    }

    // After creating a new batch, refetch once so subsequent tanks with the same
    // name resolve by ID. Skip refetch if we already know this batch name.
    if (resolvedBatchName && !batchByName.has(batchInput!)) {
      const refreshedBatches = await getBatches()
      batchByName = new Map(refreshedBatches.map((b) => [b.name, b.id]))
    }
  }

  return results
}

export const useStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tankInfo: TankValuesArrayType) => stockAPI(tankInfo),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tankInfo'] })
    },

    onError: (error) => {
      console.log('Stock API Error:', error)
      toast.error('Failed to stock tanks. Please try again.', { duration: 5000 })
    },
  })
}
